import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarClock, Loader2 } from 'lucide-react';

export const FOLLOW_UP_STAGES = ['contactado', 'calificado', 'en_visitas', 'en_negociacion'];
const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] h-11 rounded-xl [color-scheme:dark]";
const lbl = "text-white/50 text-xs uppercase tracking-wider mb-1.5 block";
const localDate = (offset = 0) => { const d = new Date(); d.setDate(d.getDate() + offset); return d.toLocaleDateString('en-CA'); };

// Mandatory: cannot be closed until the next follow-up date is saved.
export const FollowUpDialog = ({ lead, onDone, reschedule = false }) => {
  const [form, setForm] = useState({ due_date: localDate(1), due_time: '', title: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!lead) return;
    const keep = reschedule && lead.next_follow_up_date && lead.next_follow_up_date >= localDate(0);
    setForm(keep ? { due_date: lead.next_follow_up_date, due_time: lead.next_follow_up_time || '', title: lead.next_follow_up_title || '' }
      : { due_date: localDate(1), due_time: '', title: '' });
  }, [lead, reschedule]);
  if (!lead) return null;

  const save = async () => {
    if (!form.due_date) return toast.error('Elige la fecha del siguiente seguimiento');
    setSaving(true);
    try { await api.post(`/crm/leads/${lead.id}/follow-up`, { ...form, reschedule }); toast.success('Próximo seguimiento agendado'); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent data-testid="follow-up-dialog" onEscapeKeyDown={e => e.preventDefault()} onPointerDownOutside={e => e.preventDefault()}
        onOpenAutoFocus={e => e.preventDefault()} className="bg-[#0A1F1A] border-[#1A3B32] text-white w-[calc(100%-1.5rem)] max-w-md rounded-2xl [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="font-outfit text-lg flex items-center gap-2"><CalendarClock size={18} className="text-[#A38560]" />¿Cuándo es el siguiente seguimiento?</DialogTitle>
          <DialogDescription className="text-white/50 text-sm">{lead.full_name}: agenda la próxima tarea para no perder el lead.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0"><Label className={lbl}>Fecha *</Label>
              <Input data-testid="follow-up-date" type="date" min={localDate(0)} value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className={`${inputCls} w-full min-w-0`} /></div>
            <div className="min-w-0"><Label className={lbl}>Hora (opcional)</Label>
              <Input data-testid="follow-up-time" type="time" value={form.due_time} onChange={e => setForm({ ...form, due_time: e.target.value })} className={`${inputCls} w-full min-w-0`} /></div>
          </div>
          <div><Label className={lbl}>¿Qué vas a hacer?</Label>
            <Input data-testid="follow-up-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ej: Llamar para agendar visita" className={inputCls} /></div>
          <button onClick={save} disabled={saving} data-testid="follow-up-save"
            className="w-full flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />}Agendar seguimiento
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
